import SearchablePicker from "./SearchablePicker";

/**
 * EmployeePicker — a thin wrapper around SearchablePicker preconfigured for
 * user/employee objects (id, name, role, email). Handles allow-empty with
 * a synthetic "unassigned" option so any caller can clear the selection.
 *
 * Props:
 *  - employees          array of {id, name, role, email}
 *  - value              the currently selected employee id (or "" / null)
 *  - onChange(id, emp)  called with the picked id (or "") and the full employee (or null)
 *  - placeholder        trigger placeholder
 *  - testid             data-testid prefix for automation
 *  - allowUnassigned    show "— Unassigned —" first row (default true)
 *  - disabled           lock the trigger
 *  - roles              optional list of role names to keep in the list
 */
export default function EmployeePicker({
  employees = [],
  value,
  onChange,
  placeholder = "Pick employee…",
  testid = "employee-picker",
  allowUnassigned = true,
  disabled = false,
  roles = null,
}) {
  const filteredRoles = roles ? employees.filter((e) => roles.includes(e.role)) : employees;
  const items = allowUnassigned
    ? [{ id: "__none__", name: "— Unassigned —", role: "", _sentinel: true }, ...filteredRoles]
    : filteredRoles;

  return (
    <SearchablePicker
      items={items}
      value={value || (allowUnassigned ? "__none__" : "")}
      onChange={(id) => {
        if (id === "__none__") { onChange(""); return; }
        const emp = employees.find((e) => e.id === id) || null;
        onChange(id, emp);
      }}
      matchItem={employeeMatch}
      renderRow={renderEmployeeRow}
      selectedLabel={(e) => e._sentinel ? "— Unassigned —" : `${e.name}${e.role ? " · " + e.role : ""}`}
      placeholder={placeholder}
      testid={testid}
      emptyHint="No employees match your search."
      disabled={disabled}
    />
  );
}

export const employeeMatch = (e, needle) => {
  if (e._sentinel) return needle.length === 0; // sentinel only visible when no search
  return [e.name, e.email, e.role, e.mobile]
    .some((v) => v && String(v).toLowerCase().includes(needle));
};

export const renderEmployeeRow = (e) => (
  <div>
    <div className={`text-sm ${e._sentinel ? "text-[#9CA3AF] italic" : "font-semibold text-[#0a0a0a]"}`}>{e.name}</div>
    {!e._sentinel && (
      <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF]">
        {e.role}{e.email ? ` · ${e.email}` : ""}
      </div>
    )}
  </div>
);
